import { supabase } from './supabaseClient';
import { RetentionBackfill } from '../types';

/**
 * Alt-Einbehalte (Tabelle invoice_retention_backfill, Migration Nr. 107).
 *
 * Ausgestellte Belege sind eingefroren (Nr. 105). Was der Auftraggeber auf
 * Belege aus der Zeit vor dem Feature oder auf externe Alt-Abschlaege
 * einbehalten hat, steht deshalb nicht am Beleg, sondern hier: je Vorrechnung
 * (source_ref = dieselbe Kennung wie advanceInvoiceId im Abzugsblock) ein
 * Betrag. priorRetentionsFromDeductions summiert ihn zum gespeicherten
 * Einbehalt des Belegs dazu — ohne den Eintrag hielte das kumulative Modell
 * den vollen Satz auf dem naechsten Beleg ein zweites Mal ein
 * (docs/sicherheitseinbehalt-plan.md §16.2).
 *
 * Stil wie invoiceCostCenters: das LESEN ist fail-open, solange nur die
 * Tabelle fehlt; jeder andere Fehler wird geworfen. Das SCHREIBEN wirft immer —
 * wer speichert, muss erfahren, dass nichts gespeichert wurde.
 */

export const RETENTION_BACKFILL_TABLE = 'invoice_retention_backfill';

const SELECT =
    'id, project_id, source_ref, invoice_number, invoice_date, retention_cents, note';

/**
 * Fehlercode eines Supabase-Fehlers, soweit vorhanden. Supabase wirft
 * schlichte Objekte, keine Error-Instanzen — `code` ist nur ueber einen Cast
 * erreichbar.
 */
const errorCode = (e: unknown): string =>
    typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/**
 * Die Tabelle gibt es noch nicht: PGRST205 meldet PostgREST ("could not find
 * the table in the schema cache"), 42P01 ist der Postgres-Code fuer
 * undefined_table. Welcher ankommt, haengt daran, ob PostgREST das Schema
 * schon kennt.
 */
const isMissingTable = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};

/**
 * Geldspalten sind BIGINT und kommen von PostgREST als String — Number() ist
 * die einzige Konvertierung, wie im Row-Mapper von invoiceService.
 */
const mapRow = (row: any): RetentionBackfill => ({
    id: row.id,
    projectId: row.project_id,
    sourceRef: row.source_ref,
    invoiceNumber: row.invoice_number ?? '',
    invoiceDate: row.invoice_date ?? undefined,
    retentionCents: Number(row.retention_cents ?? 0),
    note: row.note ?? undefined,
});

/** Die Eintraege eines Projekts plus die Aussage, ob die Tabelle da ist. */
export interface RetentionBackfillState {
    entries: RetentionBackfill[];
    /**
     * false: Migration Nr. 107 ist in dieser Umgebung nicht gelaufen. Die
     * Maske sagt es an, statt eine leere Liste als "keine Alt-Einbehalte"
     * auszugeben; der Rechenweg rechnet mit der leeren Liste weiter.
     */
    available: boolean;
}

/**
 * Alle Alt-Einbehalte eines Projekts, aelteste Vorrechnung zuerst.
 *
 * Fail-open NUR bei fehlender Tabelle: der Rechnungseditor und die
 * Projektpruefung lesen diese Liste bei jedem Speichern, und eine nicht
 * gefahrene Migration darf sie nicht aufreissen. Ein Rechtefehler oder ein
 * Netzfehler wird dagegen geworfen — sonst rechnete ein Beleg still ohne die
 * Alt-Einbehalte und hielte sie ein zweites Mal ein.
 */
export const fetchRetentionBackfillState = async (
    projectId: string,
): Promise<RetentionBackfillState> => {
    const { data, error } = await supabase
        .from(RETENTION_BACKFILL_TABLE)
        .select(SELECT)
        .eq('project_id', projectId)
        .order('invoice_date', { ascending: true, nullsFirst: true })
        .order('invoice_number', { ascending: true });
    if (error) {
        if (isMissingTable(error)) return { entries: [], available: false };
        throw error;
    }
    return { entries: (data ?? []).map(mapRow), available: true };
};

/** Nur die Liste — fuer den Rechenweg, der die Verfuegbarkeit nicht braucht. */
export const fetchRetentionBackfill = async (projectId: string): Promise<RetentionBackfill[]> =>
    (await fetchRetentionBackfillState(projectId)).entries;

/**
 * Schreibt den Alt-Einbehalt EINER Vorrechnung — genau ein Eintrag je
 * (project_id, source_ref), der eindeutige Index in Nr. 107 setzt das durch.
 *
 * Ein Betrag <= 0 loescht den Eintrag statt ihn zu schreiben: die Tabelle
 * laesst nur positive Betraege zu (CHECK retention_cents > 0), und "0" in der
 * Maske heisst "auf diese Vorrechnung wurde nichts einbehalten" — also kein
 * Eintrag. Zurueck kommt dann ein Eintrag mit leerer id und dem Betrag 0,
 * damit der Aufrufer die Zeile ohne Sonderfall aktualisieren kann.
 */
export const upsertRetentionBackfill = async (
    projectId: string,
    entry: Omit<RetentionBackfill, 'id' | 'projectId'>,
): Promise<RetentionBackfill> => {
    const sourceRef = entry.sourceRef.trim();
    if (!sourceRef) throw new Error('Der Alt-Einbehalt braucht die Kennung der Vorrechnung.');

    if (!(entry.retentionCents > 0)) {
        const { error } = await supabase
            .from(RETENTION_BACKFILL_TABLE)
            .delete()
            .eq('project_id', projectId)
            .eq('source_ref', sourceRef);
        if (error) throw error;
        return {
            id: '',
            projectId,
            sourceRef,
            invoiceNumber: entry.invoiceNumber,
            invoiceDate: entry.invoiceDate,
            retentionCents: 0,
            note: entry.note,
        };
    }

    const { data, error } = await supabase
        .from(RETENTION_BACKFILL_TABLE)
        .upsert(
            {
                project_id: projectId,
                source_ref: sourceRef,
                invoice_number: entry.invoiceNumber,
                // Leer heisst NULL: die Spalte ist ein date, und '' ist keins.
                invoice_date: entry.invoiceDate || null,
                retention_cents: Math.round(entry.retentionCents),
                note: entry.note?.trim() || null,
                updated_at: new Date().toISOString(),
            },
            { onConflict: 'project_id,source_ref' },
        )
        .select(SELECT)
        .single();
    if (error) throw error;
    return mapRow(data);
};

export const deleteRetentionBackfill = async (id: string): Promise<void> => {
    const { error } = await supabase.from(RETENTION_BACKFILL_TABLE).delete().eq('id', id);
    if (error) throw error;
};
